import { useEffect, useState, useCallback } from "react";
import "./TrainerDashboard.css";
import "../common/lms-pages.css";
import { getQuizzes } from "../../../services/QuizService";
import { toast } from '../common/Toast/toast';
import { getBatches } from "../../../services/BatchService";

import {
  getStudentAttendance as getTrainerStudentAttendance,
  getStudentQuizHistory as getTrainerStudentQuizHistory,
} from "../../../services/TrainerDashboardService";

const StudentDetailModal = ({ 
  show, 
  onHide, 
  studentData, 
  contactPersons = [],
  fetchAttendance = getTrainerStudentAttendance,
  fetchQuizzes = getTrainerStudentQuizHistory
}) => {
  // console.log("student data",studentData)
  /* Present / absent / percentage / tone, counted by the API over every
     session of the batch (utils/studentAttendanceSummary.js). This used to
     count the page of rows it was sent -- the latest ten -- so the figure
     described the last ten classes, not the batch. */
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [quizHistory, setQuizHistory] = useState([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [loadingQuiz, setLoadingQuiz] = useState(false);
  const [quizzesData, setQuizzesData] = useState([]);
  const [batchesData, setBatchesData] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [tab, setTab] = useState('details');

  useEffect(() => {
    if (!show) return;
    if (Array.isArray(studentData?.attendanceRecords)) {
      setAttendanceRecords(studentData.attendanceRecords);
    }
    if (Array.isArray(studentData?.quizHistory)) {
      setQuizHistory(studentData.quizHistory);
    }
  }, [show, studentData?.attendanceRecords, studentData?.quizHistory]);

  // A newly opened student always starts on Details rather than on whichever
  // tab the last one was left showing.
  useEffect(() => {
    if (show) setTab('details');
  }, [show, studentData?.id, studentData?.studentId]);

  // Get batch name from batch ID
  const getBatchName = useCallback((bid) => {
    if (studentData?.batchName && String(studentData.batchId) === String(bid)) {
      return studentData.batchName;
    }
    if (!bid || !batchesData.length) return "N/A";
    
    const batch = batchesData.find(b => b.id === bid);
    return batch ? batch.name : bid;
  }, [batchesData, studentData?.batchName, studentData?.batchId]);

  // Note: getQuizzesData is kept for legacy support but won't be called automatically
  const getQuizzesData = useCallback(async (force = false) => {
    if (!force && quizzesData.length > 0) return;
    
    try {
      const response = await getQuizzes();
      if (response && response.data) {
        setQuizzesData(response.data);
      }
    } catch (error) {
      console.error("Error fetching quizzes:", error);
    }
  }, [quizzesData.length]);

  const getBatchesData = useCallback(async () => {
    try {
      setLoadingBatches(true);
      const response = await getBatches(50000000);
      setBatchesData(response.data || []);
    } catch (error) {
      toast.error("Failed to fetch batches");
      console.error("Error fetching batches:", error);
    } finally {
      setLoadingBatches(false);
    }
  }, []);

  const getQuizName = useCallback((quizId) => {
    const quiz = quizzesData.find(q => q.id === quizId);
    return quiz ? quiz.title : quizId || "N/A";
  }, [quizzesData]);

  const getAttendanceData = useCallback(async () => {
    if (!studentData?.student?.id || !studentData?.batchId) return;

    try {
      setLoadingAttendance(true);
      // Never show the previous student's figures while this one loads.
      setAttendanceSummary(null);
      const response = await fetchAttendance({
        batchId: studentData.batchId,
        studentId: studentData.student.id,
        trainerId: studentData.trainerId
      });

      if (response && response.data) {
        setAttendanceRecords(response.data || []);
        setAttendanceSummary(response.meta?.summary || null);
      }
    } catch (error) {
      console.log("Error fetching attendance:", error);
      toast.error("Failed to fetch attendance history");
    } finally {
      setLoadingAttendance(false);
    }
  }, [studentData, fetchAttendance]);

  const fetchQuizHistory = useCallback(async () => {
    const studentId = studentData?.student?.id;
    const batchId = studentData?.batchId;

    if (!studentId || !batchId) return;

    try {
      setLoadingQuiz(true);
      const response = await fetchQuizzes({
        studentId,
        batchId,
        trainerId: studentData.trainerId
      });

      if (response && response.data) {
        setQuizHistory(response.data || []);
      }
    } catch (error) {
      console.log("Error fetching quiz history:", error);
      toast.error("Failed to fetch quiz history");
    } finally {
      setLoadingQuiz(false);
    }
  }, [studentData, fetchQuizzes]);

  useEffect(() => {
    // Only fetch batches if we don't have the current batch name passed in
    if (show && !studentData?.batchName && batchesData.length === 0) {
      getBatchesData();
    }
  }, [show, getBatchesData, batchesData.length, studentData?.batchName]);

  useEffect(() => {
    // A caller that already has the API's summary (the admin dashboard,
    // from /dashboard/fullstudent) hands it in whole.
    if (show && studentData?.attendanceSummary) {
      setAttendanceSummary(studentData.attendanceSummary);
    }
  }, [show, studentData?.attendanceSummary]);

  useEffect(() => {
    if (show && studentData?.student && studentData?.batchId) {
      // If we don't have attendance summary pre-loaded, fetch it
      if (!studentData.attendanceSummary) {
        getAttendanceData();
      }
      fetchQuizHistory();
    }
  }, [show, studentData, getAttendanceData, fetchQuizHistory]);

  useEffect(() => {
    // We now get quizNames from the studentQuizHistory API directly, 
    // but we can still fetch all quizzes as a supplementary map if really needed.
    // For now, let's skip it to improve performance.
    /*
    if (show && quizzesData.length === 0) {
      getQuizzesData();
    }
    */
  }, [show, getQuizzesData, quizzesData.length]);

  if (!studentData || !studentData.student) {
    return null;
  }

  const { student, batchId, batchName } = studentData;

  if (!show) return null;

  // null tone = no session held yet (or the figures did not load): shown
  // as "—" rather than a 0% that would read as a failing student.
  const summary = attendanceSummary;
  const TONE_CLASS = { high: ' sdm-figure--good', mid: ' sdm-figure--warn', low: ' sdm-figure--bad' };

  // Batches arrive whole on the student record, so the trainer and course
  // are already on them; where they are not, fall back to the single batch
  // the record names.
  const batchRows =
    student?._assignedBatches && student._assignedBatches.length > 0
      ? student._assignedBatches
      : studentData?.batchId
        ? [{ id: studentData.batchId, name: studentData.batchName }]
        : [];

  const trainerOf = (b) =>
    b?.trainerName ||
    b?.trainerId?.trainerName ||
    b?.trainerDetails?.trainerName ||
    (typeof b?.trainerId === 'string' ? null : b?.trainer?.name) ||
    null;

  const TABS = [
    { key: 'details', label: 'Details' },
    { key: 'batches', label: 'Batches', n: batchRows.length },
    // Every session of the batch, not just the page of rows loaded.
    { key: 'attendance', label: 'Attendance', n: summary?.totalSessions ?? attendanceRecords.length },
    { key: 'quizzes', label: 'Quizzes', n: quizHistory.length },
  ];

  return (
    <div
      className="pg-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sdm-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onHide();
      }}
    >
      <div className="pg-modal-panel pg-modal-panel--xl">
        <header className="pg-modal-head">
          <div className="pg-modal-heading">
            <span className="pg-modal-eyebrow">Student</span>
            <h3 className="pg-modal-title" id="sdm-title">
              {student?.name || studentData?.name || 'Student'}
            </h3>
            {(student?.email || studentData?.email) && (
              <p className="pg-modal-sub">
                {student?.email || studentData?.email}
              </p>
            )}
          </div>
          <button
            type="button"
            className="pg-modal-close"
            onClick={onHide}
            aria-label="Close"
          >
            &times;
          </button>
        </header>

        {/* Three faces: who they are, whether they turn up, how they score. */}
        <div className="lp-tabs" role="tablist" aria-label="Student">
          {TABS.map((t) => (
            <button
              type="button"
              key={t.key}
              role="tab"
              id={`sdm-tab-${t.key}`}
              aria-selected={tab === t.key}
              aria-controls={`sdm-panel-${t.key}`}
              className={`lp-tab${tab === t.key ? ' is-on' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
              {t.n !== undefined && <span className="lp-tab-n">{t.n}</span>}
            </button>
          ))}
        </div>

        <div className="pg-modal-body">
          {studentData?.loading ? (
            <div className="att-picklist-foot">
              <span className="pg-spinner att-spinner" aria-hidden="true" />
              <span>Loading this student...</span>
            </div>
          ) : (
            <>
              {tab === 'details' && (
                <div
                  role="tabpanel"
                  id="sdm-panel-details"
                  aria-labelledby="sdm-tab-details"
                >
                  {/* Turnout first: it is the thing most often being
                      checked, and it frames everything under it. */}
                  <div className="sdm-figures">
                    {/* This counted sessions, not batches -- it was the
                        number of attendance rows loaded -- so it is labelled
                        for what it is. */}
                    <div className="sdm-figure">
                      <span className="sdm-figure-label">Sessions</span>
                      <span className="sdm-figure-value">{summary ? summary.totalSessions : '—'}</span>
                    </div>
                    <div className="sdm-figure">
                      <span className="sdm-figure-label">Present</span>
                      <span className="sdm-figure-value">{summary ? summary.present : '—'}</span>
                    </div>
                    <div className="sdm-figure">
                      <span className="sdm-figure-label">Absent</span>
                      <span className="sdm-figure-value">{summary ? summary.absent : '—'}</span>
                    </div>
                    <div className={`sdm-figure${TONE_CLASS[summary?.tone] || ''}`}>
                      <span className="sdm-figure-label">Attendance</span>
                      <span className="sdm-figure-value">
                        {summary?.tone ? `${summary.attendancePercentage}%` : '—'}
                      </span>
                    </div>
                  </div>

                  <dl className="quiz-facts">
                    <div>
                      <dt>Name</dt>
                      <dd>{student?.name || studentData?.name || '—'}</dd>
                    </div>
                    <div>
                      <dt>Email</dt>
                      <dd>{student?.email || studentData?.email || '—'}</dd>
                    </div>
                    <div>
                      <dt>Phone</dt>
                      <dd>{student?.phone || studentData?.phone || '—'}</dd>
                    </div>
                    <div>
                      <dt>Batches</dt>
                      <dd>{batchRows.length}</dd>
                    </div>
                  </dl>

                  {contactPersons.length > 0 && (
                    <>
                      <h5 className="pgm-section-title">
                        Contact people
                        <span className="pgm-section-count">
                          {contactPersons.length}
                        </span>
                      </h5>
                      <div className="lp-dialog-table">
                        <table>
                          <thead>
                            <tr>
                              <th>Sr. No</th>
                              <th>Name</th>
                              <th>Relation</th>
                              <th>Phone</th>
                            </tr>
                          </thead>
                          <tbody>
                            {contactPersons.map((cp, idx) => (
                              <tr key={cp.id || idx}>
                                <td>{idx + 1}</td>
                                <td>{cp.name || cp.contactPersonName || '—'}</td>
                                <td>{cp.relation || cp.relationship || '—'}</td>
                                <td>{cp.phone || cp.contactNumber || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}

              {tab === 'batches' && (
                <div
                  role="tabpanel"
                  id="sdm-panel-batches"
                  aria-labelledby="sdm-tab-batches"
                >
                  {loadingBatches ? (
                    <div className="att-picklist-foot">
                      <span className="pg-spinner att-spinner" aria-hidden="true" />
                      <span>Loading batches...</span>
                    </div>
                  ) : batchRows.length === 0 ? (
                    <p className="lp-muted">
                      This student is not assigned to a batch.
                    </p>
                  ) : (
                    <div className="lp-dialog-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Sr. No</th>
                            <th>Batch</th>
                            <th>Course</th>
                            <th>Trainer</th>
                            <th>Mode</th>
                          </tr>
                        </thead>
                        <tbody>
                          {batchRows.map((b, idx) => (
                            <tr key={b.id || b._id || idx}>
                              <td>{idx + 1}</td>
                              <td>{b.name || getBatchName(b.id || b._id)}</td>
                              <td>
                                {b.courseName ||
                                  b.courseId?.name ||
                                  b.courseDetails?.name ||
                                  '—'}
                              </td>
                              <td>{trainerOf(b) || '—'}</td>
                              <td>{b.mode || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {tab === 'attendance' && (
                <div
                  role="tabpanel"
                  id="sdm-panel-attendance"
                  aria-labelledby="sdm-tab-attendance"
                >
                  {loadingAttendance ? (
                    <div className="att-picklist-foot">
                      <span className="pg-spinner att-spinner" aria-hidden="true" />
                      <span>Loading attendance...</span>
                    </div>
                  ) : attendanceRecords.length === 0 ? (
                    <p className="lp-muted">No attendance recorded yet.</p>
                  ) : (
                    <div className="lp-dialog-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Sr. No</th>
                            <th>Date</th>
                            <th>Subject</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {attendanceRecords.map((record, idx) => (
                            <tr key={record.id || idx}>
                              <td>{idx + 1}</td>
                              <td>
                                {record.date
                                  ? new Date(record.date).toLocaleDateString()
                                  : '—'}
                              </td>
                              <td>{record.subject || '—'}</td>
                              <td>
                                <span
                                  className={`pg-pill ${
                                    record.status === 'Present'
                                      ? 'pg-pill--good'
                                      : 'pg-pill--bad'
                                  }`}
                                >
                                  {record.status || 'Unknown'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {tab === 'quizzes' && (
                <div
                  role="tabpanel"
                  id="sdm-panel-quizzes"
                  aria-labelledby="sdm-tab-quizzes"
                >
                  {loadingQuiz ? (
                    <div className="att-picklist-foot">
                      <span className="pg-spinner att-spinner" aria-hidden="true" />
                      <span>Loading quizzes...</span>
                    </div>
                  ) : quizHistory.length === 0 ? (
                    <p className="lp-muted">No quiz has been taken yet.</p>
                  ) : (
                    <div className="lp-dialog-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Sr. No</th>
                            <th>Quiz</th>
                            <th>Result</th>
                            <th>Score</th>
                            <th>Time taken</th>
                            <th>Submitted</th>
                          </tr>
                        </thead>
                        <tbody>
                          {quizHistory.map((quiz, idx) => (
                            <tr key={quiz.id || idx}>
                              <td>{idx + 1}</td>
                              <td>{quiz.quizName || getQuizName(quiz.quizId)}</td>
                              <td>
                                <span
                                  className={`pg-pill ${
                                    quiz.result === 'Pass'
                                      ? 'pg-pill--good'
                                      : 'pg-pill--bad'
                                  }`}
                                >
                                  {quiz.result || 'Unknown'}
                                </span>
                              </td>
                              <td>
                                {quiz.scored !== undefined
                                  ? `${quiz.scored}/${quiz.outOf || '?'}`
                                  : quiz.score || '—'}
                              </td>
                              <td>{quiz.timeConsumed || '—'}</td>
                              <td>
                                {quiz.submissionDate
                                  ? new Date(quiz.submissionDate).toLocaleDateString()
                                  : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <footer className="pg-modal-foot">
          <button type="button" className="pg-btn pg-btn--main" onClick={onHide}>
            Close
          </button>
        </footer>
      </div>
    </div>
  );
};

export default StudentDetailModal;
